/**
 * Core Breadcrumbs Component
 *
 * Navigation breadcrumb trail with clickable links, icons, overflow collapse,
 * and custom separator. Built on MUI Breadcrumbs.
 *
 * @example
 *   <Breadcrumbs items={[
 *     { label: 'Home', onClick: () => nav('home') },
 *     { label: 'Data Sources', onClick: () => nav('datasources') },
 *     { label: 'PostgreSQL' }, // last item is current (not clickable)
 *   ]} />
 */
import React from 'react';
import { Breadcrumbs as MuiBreadcrumbs, Typography, Link } from '@mui/material';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  /** Click handler — absent on last item means it's the current page */
  onClick?: () => void;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Href for native link (alternative to onClick) */
  href?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  /** Maximum items before collapsing with "..." */
  maxItems?: number;
  /** Custom separator */
  separator?: React.ReactNode;
  /** Show home icon on first item */
  showHomeIcon?: boolean;
  /** Size */
  size?: 'sm' | 'md';
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  maxItems = 6,
  separator,
  showHomeIcon = false,
  size = 'sm',
  className = '',
}) => {
  const fontSize = size === 'sm' ? '0.75rem' : '0.8125rem';
  const sep = separator || <ChevronRight className="h-3 w-3 text-slate-300" />;

  return (
    <MuiBreadcrumbs
      separator={sep}
      maxItems={maxItems}
      className={className}
      sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
    >
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        const isFirst = idx === 0;
        const icon = isFirst && showHomeIcon
          ? (item.icon || <Home className="h-3.5 w-3.5" />)
          : item.icon;

        if (isLast) {
          return (
            <Typography
              key={idx}
              variant="body2"
              color="text.primary"
              sx={{ fontSize, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {icon && <span className="flex items-center">{icon}</span>}
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={idx}
            component="button"
            underline="hover"
            color="text.secondary"
            onClick={item.onClick || (() => item.href && (window.location.href = item.href))}
            sx={{
              fontSize,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {icon && <span className="flex items-center">{icon}</span>}
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};
export default Breadcrumbs;
